import { HttpLink } from 'apollo-angular/http';
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { ToastrService } from 'ngx-toastr';
import { Observable, of as observableOf, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import * as SparkMD5 from 'spark-md5';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import { LoaderService } from 'src/app/modules/core/services/loader.service';
import { Helper } from '../classes/helper';
import { FileType } from '../enums/file-type.enum';
import { NotifyOrConfirmType } from '../enums/notify-or-confirm-type.enum';
import { FileData } from '../models/file-data.model';
import { FileUploadResponseData } from '../models/file-upload-response-data.model';
import { FileSchema } from '../schemas/file.schema';
import { GraphqlService } from './graphql.service';
import { LoggerService } from './logger.service';
import { NotifyOrConfirmService } from './notify-or-confirm.service';
import { SessionService } from './session.service';
import { UserService } from './user.service';
import { WindowRefService } from './window.service';

/**
 * Service for file handling
 */
@Injectable({
	providedIn: 'root'
})
export class FileService extends GraphqlService {
	// =====================================================================================================================================
	// Constructor
	// =====================================================================================================================================

	// Include services
	constructor(
		// Services for GraphqlService
		protected http: HttpClient,
		protected httpLink: HttpLink,
		protected loaderService: LoaderService,
		protected configService: ConfigService,
		protected sessionService: SessionService,
		protected router: Router,
		protected toastrService: ToastrService,
		protected windowRef: WindowRefService,
		// Additional Services
		protected notification: NotifyOrConfirmService,
		protected translateService: TranslateService,
		protected userService: UserService,
		protected logger: LoggerService,
		protected ngZone: NgZone,
		protected sanitizer: DomSanitizer
	) {
		// Init GraphqlService
		super(
			http,
			httpLink,
			configService,
			loaderService,
			notification,
			sessionService,
			translateService,
			router,
			logger,
			ngZone,
			toastrService,
			windowRef
		);
	}

	// =====================================================================================================================================
	// API methods
	// =====================================================================================================================================

	/**
	 * Upload File
	 *
	 * @param file - File to be uploaded
	 * @param userUid - Uid of the user uploading the file
	 * @param type - Type of file
	 * @param resolverFunction - Resolver function that, among other things, accepts the subscriber and sets the status accordingly
	 * @param errorNotification - Setting whether errors are to be output or not
	 */
	public uploadFile(
		file: File,
		userUid: string = this.sessionService.currentUser.uid,
		type: FileType,
		resolverFunction: (
			userUid: string,
			fileUid: string,
			fileData: FileData,
			subscriber: Subscriber<FileData>,
			errorNotification: boolean,
			...args: any[]
		) => any,
		errorNotification: boolean = true
	): Observable<FileData> {
		return new Observable((subscriber) => {
			// 1. Create MD5 hash of the file
			this.md5HashOfFile(file, true).subscribe(
				(md5Hash) => {
					// 2. Inform DataService about new file and fetch new uid
					this.addFile(file, md5Hash, type).subscribe(
						(response) => {
							// Check file uid
							if (!response || !response.uid) {
								this.processError(new Error('Invalid fileUid of uploaded file'), errorNotification, subscriber);
								return;
							}

							// 3. Upload file
							this.postFile(file, 'uploadFile/' + response.uid).subscribe(
								() => {
									// 4. Get file data
									this.getFileData(response.uid).subscribe(
										(fileData) => {
											// Check file link
											if (!fileData || !fileData.link) {
												this.processError(new Error('Missing link for uploaded file'), errorNotification, subscriber);
												return;
											}

											// 5. Execute resolver function
											resolverFunction(userUid, response.uid, fileData, subscriber, errorNotification);
										},
										(error) => {
											// Error Handling when retrieving the file URL
											this.processError(error, errorNotification, subscriber);
										}
									);
								},
								(error) => {
									// Error handling during the upload process
									this.processError(error, errorNotification, subscriber);
								}
							);
						},
						(error) => {
							// Error Handling when retrieving the avatar file uid
							this.processError(error, errorNotification, subscriber);
						}
					);
				},
				(error) => {
					// Error Handling when retrieving md5 hash
					this.processError(error, errorNotification, subscriber);
				}
			);
		});
	}

	/**
	 * Upload avatar file
	 */
	public uploadAvatarFile(
		file: File,
		userUid: string = this.sessionService.currentUser.uid,
		errorNotification: boolean = true
	): Observable<FileData> {
		// Resolver function
		const resolverFunc = (
			rfUserUid: string,
			rfFileUid: string,
			rfFileData: FileData,
			rfSubscriber: Subscriber<FileData>,
			rfErrorNotification: boolean
		) => {
			// Update avatar info in user
			this.userService.update(rfUserUid, null, undefined, { avatarGuid: rfFileUid }).subscribe(
				(userResponse) => {
					if (userResponse.uid === this.sessionService.currentUser.uid) {
						this.sessionService.currentUser.map(userResponse);
					}
					rfSubscriber.next(rfFileData);
					rfSubscriber.complete();
				},
				(error) => {
					// Error Handling when saving avatarGuid
					this.processError(error, rfErrorNotification, rfSubscriber);
				}
			);
		};

		// Upload file
		return this.uploadFile(file, userUid, FileType.AVATAR, resolverFunc, errorNotification);
	}

	/**
	 * Get data of avata file
	 */
	public getAvatarData(externalUid: string): Observable<FileData> {
		return this.getFileData(externalUid, 'avatar/');
	}

	/**
	 * Upload signature file and text
	 */
	public uploadSignatureFile(
		file: File,
		text: string,
		userUid: string = this.sessionService.currentUser.uid,
		errorNotification: boolean = true
	): Observable<FileData> {
		// Resolver function
		const resolverFunc = (
			rfUserUid: string,
			rfFileUid: string,
			rfFileData: FileData,
			rfSubscriber: Subscriber<FileData>,
			rfErrorNotification: boolean
		) => {
			// Update signature data in user
			this.userService
				.update(rfUserUid, {
					signatureData: {
						signatureFileId: rfFileUid
					}
				})
				.subscribe(
					(userResponse) => {
						if (userResponse.uid === this.sessionService.currentUser.uid) {
							this.sessionService.currentUser.map(userResponse);
						}
						rfSubscriber.next(rfFileData);
						rfSubscriber.complete();
					},
					(error) => {
						// Error Handling when saving avatarGuid
						this.processError(error, rfErrorNotification, rfSubscriber);
					}
				);
		};

		// Upload file
		return this.uploadFile(file, userUid, FileType.SIGNATURE, resolverFunc, errorNotification);
	}

	/**
	 * Get data of avata file
	 */
	public getSignatureData(fileId: string): Observable<FileData> {
		return this.getFileData(fileId);
	}

	/**
	 * Get file from url
	 *
	 * Returns SafeResourceURL if config.trusted === true, else a string
	 */
	public getFileFromURL(
		url: string | SafeResourceUrl,
		config?: { addTimeStamp?: boolean; timeStampPrefix?: string; trusted?: boolean }
	): Observable<string | SafeResourceUrl> {
		// Process config
		config = Object.assign({ addTimeStamp: false, timeStampPrefix: '&ts=', trusted: false }, config);

		// Return observable
		return new Observable<string | SafeResourceUrl>((subscriber) => {
			// Check url
			if (!url) {
				subscriber.error('Missing url');
				return;
			}

			// Set timestamp
			if (config.addTimeStamp) {
				url = url + config.timeStampPrefix + new Date().getTime();
			}

			// Get blob
			this.http.get(url.toString(), { responseType: 'blob' }).subscribe(
				(file) => {
					// Convert blob to file
					const reader = this.getNewFileReader();
					reader.addEventListener(
						'load',
						() => {
							// Check for errors
							if (reader.error) {
								subscriber.error(reader.error);
								return;
							}

							// (Trusted) file
							let trustedFile: string | SafeResourceUrl = reader.result;
							if (config.trusted) {
								trustedFile = this.sanitizer.bypassSecurityTrustResourceUrl(<string>trustedFile);
							}

							// Return file
							subscriber.next(trustedFile);
							subscriber.complete();
						},
						false
					);

					if (file) {
						reader.readAsDataURL(file);
					} else {
						subscriber.error('File could not be loaded: ' + url);
					}
				},
				(error) => {
					// catch errors if File could not be loaded, e.g. bad request because file has moved...
					subscriber.error('File could not be loaded: ' + url);
				}
			);
		});
	}

	// =====================================================================================================================================
	// Helper methods
	// =====================================================================================================================================

	/**
	 * Get new file reader instance
	 */
	public getNewFileReader() {
		return new FileReader();
	}

	/**
	 * Get data of file
	 */
	public getFileData(id: string, urlExtension: string = 'file/'): Observable<FileData> {
		// Check uid
		if (!id) {
			return observableOf(null);
		}

		return new Observable<FileData>((subscriber) => {
			this.sessionService.check().subscribe(() => {
				this.http
					.get(this.configService.config.fileDownloadUri + urlExtension + id, {
						headers: {
							Authorization: 'Bearer ' + this.sessionService.session.accessToken
						}
					})
					.subscribe(
						(response: any) => {
							// File found
							if (response) {
								subscriber.next(FileData.map(response));
								subscriber.complete();

								// File not found
							} else {
								subscriber.next(null);
								subscriber.complete();
							}

							// Error during the http request
						},
						(error) => {
							this.logger.error(error);
							subscriber.error(error);
							this.notification.showNotification(
								this.translateService.instant('fileService.error.download.invalidFileDownloadTitle'),
								this.translateService.instant('fileService.error.download.invalidFileDownloadMessage'),
								NotifyOrConfirmType.ERROR,
								this.translateService.instant('fileService.error.download.button')
							);
						}
					);
			});
		});
	}

	/**
	 * Create md5 hash of a file
	 *
	 * See https://github.com/satazor/js-spark-md5#hash-a-file-incrementally
	 */
	public md5HashOfFile(file: File, substring: boolean = false, chunkSize = 2097152): Observable<string> {
		return new Observable((subscriber) => {
			// Init blob
			const blobSlice = File.prototype.slice || (<any>File.prototype).mozSlice || (<any>File.prototype).webkitSlice;

			// Init chunk
			const chunks = Math.ceil(file.size / chunkSize);
			let currentChunk = 0;

			// Init processors
			const spark = new SparkMD5.ArrayBuffer();
			const fileReader = this.getNewFileReader();

			// Init load next function
			const loadNext = () => {
				const start = currentChunk * chunkSize;
				const end = start + chunkSize >= file.size ? file.size : start + chunkSize;

				fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
			};

			// Error handling during process file
			fileReader.onerror = (error) => {
				this.logger.error(error);
				subscriber.error(error);
			};

			// Process file
			fileReader.onload = (event) => {
				spark.append((<any>event.target).result);
				currentChunk++;

				if (currentChunk < chunks) {
					// Go on
					loadNext();
				} else {
					let md5String = spark.end();
					if (substring) {
						// Add '-' after every second char and replace '-' at the end of the string, if exists
						md5String = md5String.replace(/(.{2})/g, '$1-').replace(/-$/, '');
					}
					subscriber.next(md5String);
					subscriber.complete();
				}
			};

			// Generate md5 hash
			loadNext();
		});
	}

	/**
	 * Upload file
	 */
	public postFile(file: File, urlExtension: string): Observable<FileUploadResponseData> {
		return new Observable((subscriber) => {
			// Create FormData
			const formData: FormData = new FormData();
			formData.append('file', file, file.name);

			this.sessionService.check().subscribe(() => {
				// Create HttpHeaders
				const headers: HttpHeaders = new HttpHeaders({
					Authorization: 'Bearer ' + this.sessionService.session.accessToken
				});

				// Create request
				const req = new HttpRequest('POST', this.configService.config.fileUploadUri + urlExtension, formData, {
					headers,
					reportProgress: false
				});

				// Upload
				this.http.request(req).subscribe(
					(event) => {
						if (event && event.type === HttpEventType.Response) {
							subscriber.next(FileUploadResponseData.map(event.body));
							subscriber.complete();
						}
					},
					(error) => {
						this.logger.error(error);
						subscriber.error(error);
					}
				);
			});
		});
	}

	// =====================================================================================================================================
	// Protected methods
	// =====================================================================================================================================

	/**
	 * Add file in DataService
	 */
	protected addFile(file: File, md5Hash: string, type: string) {
		// Init input
		const fileInput = {
			fileDate: file.lastModified ? new Date(file.lastModified) : new Date(),
			checksum: md5Hash,
			fileName: file.name,
			fileSize: file.size
		};

		const input = {};
		input[type + 'File'] = fileInput;

		// Return mutation
		return this.mutation(FileSchema['add' + Helper.upperFirst(type) + 'File'], input, { showSuccessMessage: false }).pipe(
			map((data: any) => data['add' + Helper.upperFirst(type) + 'File'])
		);
	}

	/**
	 * Process error
	 */
	protected processError(error: Error, errorNotification: boolean = true, subscriber: Subscriber<any> = null) {
		// Show notification
		if (errorNotification) {
			let errorMessage: string;
			if (error && error['networkError']?.error) {
				errorMessage = this.formatErrorText(error['networkError'].error);
			}

			if (!errorMessage) {
				errorMessage = this.translateService.instant('fileService.error.upload.invalidFileUploadMessage');
			}

			this.notification.showNotification(
				this.translateService.instant('fileService.error.upload.invalidFileUploadTitle'),
				errorMessage,
				NotifyOrConfirmType.ERROR,
				this.translateService.instant('fileService.error.upload.button')
			);
		}

		// Throw error
		if (subscriber) {
			subscriber.error(error);
		}
	}

	private formatErrorText(sourceText: string): string {
		try {
			let failureDetail = JSON.parse(sourceText);
			return `${failureDetail.Code}: ${failureDetail.AdditionalInformation}`;
		} catch {
			return null;
		}
	}
}
